import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { ToastrService } from '@app/services/toastr.service'
import { finalize, takeUntil } from 'rxjs/operators'
import { BADGE_SIDES, TOOLBAR_ACTIONS } from '../../../enums/badge.enum'
import { badgeSides } from '../../../models/badges.model'
import { BadgeEditorService, updateBadgePayload } from '../../../services/badge-editor.service'
import { BadgeSide, TemplateLayout } from '../../../types/badge.type'
const DEFAULT_DISTANCE_FOR_PUSHING = 1
const DEFAULT_DISTANCE_FOR_CONTENT_WIDTH_CHANGE_IN_MM = 2
const MINIMUM_COLUMN_WIDTH_IN_MM = 40
const MIN_CONTENT_WIDTH_PERCENT = 50

@Component({
  selector: 'app-badge-template-toolbar',
  templateUrl: './badge-template-toolbar.component.html',
  styleUrls: ['./badge-template-toolbar.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class BadgeTemplateToolbarComponent implements AfterViewInit, OnDestroy {
  @Input() public selectedTemplate!: TemplateLayout | null
  @Input() public badgeForm!: FormGroup
  public badgeSides: BadgeSide[] = badgeSides
  public badgeSide = BADGE_SIDES
  public pushedSpace: number = 0
  public contentWidthMM: number = 0
  public interval: ReturnType<typeof setInterval> | null = null
  public canBePushed: boolean = true
  public canContentWidthChange: boolean = true
  public isSaving: boolean = false
  private _destroy$ = new EventEmitter<void>()

  constructor(private _BadgeService: BadgeEditorService, private _ToastrService: ToastrService, private _ActivatedRoute: ActivatedRoute) {}

  ngAfterViewInit(): void {
    badgeSides[0].isActive = false
    this.chooseSide(badgeSides[0])
    if (this.badgeForm.value.background_preprinted === null) this.badgeForm.patchValue({ background_preprinted: false })
    this._BadgeService.toolbarAction$.pipe(takeUntil(this._destroy$)).subscribe((action) => {
      if (action.action === TOOLBAR_ACTIONS.SWITCH_SIDE) this.chooseSide(this.badgeSides.find((side) => side.badge_side === action.payload) as BadgeSide)
    })
  }

  ngOnDestroy(): void {
    this.stopAction()
    this._destroy$.next()
    this._destroy$.complete()
  }

  /**
   * @method onKeyDownPress
   * @description Listen to the keyboard event and save the badge when pressing Ctrl + S
   * @param event KeyboardEvent
   */
  @HostListener('document:keydown', ['$event'])
  public onKeyDownPress(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault()
      this.saveBadge()
    }
  }

  /**
   * @method stopAction
   * @description Stopping the continuos action by clearing the interval
   */
  @HostListener('document:mouseup')
  public stopAction(): void {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  /**
   * @method _extractMargin
   * @description Extract the margin of the badge content container
   * @returns void
   */
  private _extractMargin(): void {
    const badge = document.getElementById('badge')
    const badgeContentContainer = document.getElementById('badge-content-container')
    if (!badge || !badgeContentContainer) {
      this.pushedSpace = 0
      return
    }
    const margin = parseFloat(badgeContentContainer.style.marginBottom)
    const stepsInPercentage = (margin * this.selectedTemplate!.width) / 100
    this.pushedSpace = Math.round(stepsInPercentage)
  }

  /**
   * @method _extractContentWidth
   * @description Extract the current content container width in mm (relative to selected template width)
   */
  private _extractContentWidth(): void {
    const badge = document.getElementById('badge')
    const badgeContentContainer = document.getElementById('badge-content-container')
    if (!badge || !badgeContentContainer || !this.selectedTemplate) {
      this.contentWidthMM = this.selectedTemplate?.width || 0
      return
    }

    const badgeWidthPx = badge.getBoundingClientRect().width || 1
    const contentWidthPx = badgeContentContainer.getBoundingClientRect().width || badgeWidthPx
    const contentWidthMM = (contentWidthPx / badgeWidthPx) * this.selectedTemplate.width
    this.contentWidthMM = Math.max(0, Math.round(contentWidthMM))
  }

  /**
   * @method _getMinAllowedContentWidthMM
   * @description Compute a safe minimum content width based on current badge structure and minimum column size.
   */
  private _getMinAllowedContentWidthMM(): number {
    if (!this.selectedTemplate) return MINIMUM_COLUMN_WIDTH_IN_MM

    // New rule: minimum content width is a percentage of the template width (default 50%).
    // This decouples content-width control from the 40mm "minimum column width" constraint.
    const minWidth = (this.selectedTemplate.width * MIN_CONTENT_WIDTH_PERCENT) / 100
    return Math.max(0, Math.min(this.selectedTemplate.width, Math.ceil(minWidth)))
  }

  /**
   * @method chooseSide
   * @description Choose the side of the badge
   * @param side BadgeSide
   * @returns void
   */
  public chooseSide(side: BadgeSide = badgeSides[0]): void {
    if (side.isActive) return
    this.badgeSides.forEach((badgeSide) => (badgeSide.isActive = false))
    side.isActive = true
    this._BadgeService.toolbarAction$.next({
      action: TOOLBAR_ACTIONS.SWITCH_SIDE,
      payload: side.badge_side
    })
    setTimeout(() => {
      this._extractMargin()
      this._extractContentWidth()
    })
  }

  /**
   * @method saveBadge
   * @description Save the badge and show loading spinner while saving
   * @returns void
   */
  public saveBadge(): void {
    if (this.isSaving) return
    this.isSaving = true
    if (!this.badgeForm.get('badge_front_html_content')?.value)
      this.badgeForm
        .get('badge_front_html_content')
        ?.setValue(
          `<div id="badge" data-width="${this.selectedTemplate?.height || 50}" data-height="${
            this.selectedTemplate?.height || 50
          }" data-badgetype="5" style="background-color: rgb(255, 255, 255); width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; user-select: none; background-size: cover; background-repeat: no-repeat;"></div>`
        )
    this.badgeForm.get('badge_front_html_content')?.value
    if (this._BadgeService.isDTCM && !this.badgeForm.get('badge_front_html_content')?.value.includes('dtcm_qr_code')) {
      this.isSaving = false
      this._ToastrService.error('Error', 'DTCM QR Code is required for Dubai Event')
      return
    }
    const payload: updateBadgePayload = {
      form_id: this.badgeForm.value.id,
      badge_front_html_content: this.badgeForm.get('badge_front_html_content')?.value,
      background_preprinted: this.badgeForm.get('background_preprinted')?.value,
      front_fixed_element: this._BadgeService.frontFixedElement,
      reset: false
    }
    this._BadgeService
      .saveBadge(payload)
      .pipe(
        finalize(() => (this.isSaving = false)),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: () => {
          this._ToastrService.success('Success', 'Badge has been saved successfully')
        },
        error: () => {
          this._ToastrService.error('Error', 'Failed to save badge')
        }
      })
  }

  /**
   * @method changeTemplate
   * @description Reset the badge creation and start over a new template
   * @returns void
   *
   */
  public changeTemplate(): void {
    this._BadgeService.toolbarAction$.next({
      action: TOOLBAR_ACTIONS.START_OVER
    })
  }

  /**
   * @method pushUp
   * @description Push the content of the badge up
   * @returns void
   */
  public pushUp(): void {
    this._extractMargin()
    const badge = document.getElementById('badge')
    const badgeContentContainer = document.getElementById('badge-content-container')
    if (!badge || !badgeContentContainer) return

    const badgeBounds = badge.getBoundingClientRect()
    const badgeContentContainerBounds = badgeContentContainer.getBoundingClientRect()
    const spaceInMM = (DEFAULT_DISTANCE_FOR_PUSHING * this._BadgeService.getElementHeight('#badge')) / this.selectedTemplate!.height
    // Check if they will start overlapping
    const willStartOverlapping = badgeContentContainerBounds.top - spaceInMM < badgeBounds.top
    if (willStartOverlapping) {
      if (!this.canBePushed) return
      this.canBePushed = false
      setTimeout(() => (this.canBePushed = true), 200)
      return
    }
    this.pushedSpace += DEFAULT_DISTANCE_FOR_PUSHING
    const percentage = (this.pushedSpace / this.selectedTemplate!.width) * 100
    this._BadgeService.toolbarAction$.next({
      action: TOOLBAR_ACTIONS.PUSH_UP,
      payload: percentage
    })
  }

  /**
   * @method pushUpContinuously
   * @description Push the content of the badge up continuously
   * @returns void
   */
  public pushUpContinuously(): void {
    this._startContinuousAction(() => this.pushUp())
  }

  /**
   * @method pushDown
   * @description Push the content of the badge down
   * @returns void
   */
  public pushDown(): void {
    this._extractMargin()
    const badge = document.getElementById('badge')
    const badgeContentContainer = document.getElementById('badge-content-container')
    if (!badge || !badgeContentContainer) return

    const badgeBounds = badge.getBoundingClientRect()
    const badgeContentContainerBounds = badgeContentContainer.getBoundingClientRect()
    const spaceInMM = (DEFAULT_DISTANCE_FOR_PUSHING * this._BadgeService.getElementHeight('#badge')) / this.selectedTemplate!.height

    // Check if they will start overlapping
    const willStartOverlapping = badgeContentContainerBounds.bottom + spaceInMM >= badgeBounds.bottom
    if (willStartOverlapping) {
      if (!this.canBePushed) return
      this.canBePushed = false
      setTimeout(() => (this.canBePushed = true), 200)
      return
    }

    this.pushedSpace -= DEFAULT_DISTANCE_FOR_PUSHING
    const percentage = (this.pushedSpace / this.selectedTemplate!.width) * 100
    this._BadgeService.toolbarAction$.next({
      action: TOOLBAR_ACTIONS.PUSH_DOWN,
      payload: percentage
    })
  }

  /**
   * @method pushDownContinuously
   * @description Push the content of the badge down continuously
   * @returns void
   */
  public pushDownContinuously(): void {
    this._startContinuousAction(() => this.pushDown())
  }

  /**
   * @method stopPushing
   * @description Stop pushing the content of the badge after releasing the mouse button
   * @returns void
   */
  public stopPushing(): void {
    this.stopAction()
  }

  /**
   * @method narrowContent
   * @description Decrease badge content container width (clamped by minimum element size)
   */
  public narrowContent(): void {
    this._extractContentWidth()
    if (!this.selectedTemplate) return
    const minAllowed = this._getMinAllowedContentWidthMM()
    const next = this.contentWidthMM - DEFAULT_DISTANCE_FOR_CONTENT_WIDTH_CHANGE_IN_MM
    if (next < minAllowed) {
      if (!this.canContentWidthChange) return
      this.canContentWidthChange = false
      setTimeout(() => (this.canContentWidthChange = true), 200)
      return
    }
    this.contentWidthMM = next
    const percentage = (this.contentWidthMM / this.selectedTemplate.width) * 100
    this._BadgeService.toolbarAction$.next({
      action: TOOLBAR_ACTIONS.CONTENT_WIDTH_DECREASE,
      payload: Math.max(0, Math.min(100, percentage))
    })
  }

  public narrowContentContinuously(): void {
    this._startContinuousAction(() => this.narrowContent())
  }

  /**
   * @method widenContent
   * @description Increase badge content container width (up to 100%)
   */
  public widenContent(): void {
    this._extractContentWidth()
    if (!this.selectedTemplate) return
    const next = this.contentWidthMM + DEFAULT_DISTANCE_FOR_CONTENT_WIDTH_CHANGE_IN_MM
    this.contentWidthMM = Math.min(this.selectedTemplate.width, next)
    const percentage = (this.contentWidthMM / this.selectedTemplate.width) * 100
    this._BadgeService.toolbarAction$.next({
      action: TOOLBAR_ACTIONS.CONTENT_WIDTH_INCREASE,
      payload: Math.max(0, Math.min(100, percentage))
    })
  }

  public widenContentContinuously(): void {
    this._startContinuousAction(() => this.widenContent())
  }

  /**
   * @method _startContinuousAction
   * @description Start single continuous stream and prevent stacked intervals
   * @param action () => void
   */
  private _startContinuousAction(action: () => void): void {
    this.stopAction()
    this.interval = setInterval(action, 150)
  }
}
